import { Edit, RemoveCircle } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { ButtonLink } from '~/components/link';
import { PlaylistContainer } from '~/components/playlists/PlaylistContainer';
import { ShuffleButton } from '~/components/playlists/ShuffleButton';
import type { Zone } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import { ContentAddButton } from './ContentAddButton';
import type {
  ShowViewZone__Playlist as Playlist,
  ShowViewZone__Show as Show,
  ShowViewZone__ViewZone as ViewZone,
} from './ViewZone.generated';
import { BulkActionsMenu } from './bulk-actions-menu';

interface ViewZonePlaylistProps {
  onRemoveBulk: () => void;
  onRemovePlaylist: (playlistId: number) => void | Promise<unknown>;
  onRemovePlaylistItem: (playlistItemId: number) => void;
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  show: Show;
  viewZone: ViewZone;
  viewZonePlaylist: {
    id: number;
    playlist: Playlist;
  };
  zone: Pick<Zone, 'height' | 'name' | 'width'>;
}

export const ViewZonePlaylist = ({
  onRemoveBulk,
  onRemovePlaylist,
  onRemovePlaylistItem,
  selectedIds,
  setSelectedIds,
  show,
  viewZone,
  viewZonePlaylist,
  zone,
}: ViewZonePlaylistProps) => {
  const { t } = useTranslation();
  const link = useLink();
  const playlist = viewZonePlaylist.playlist;
  const isReusable = playlist.reusable;

  const toolbarActions = isReusable
    ? [
        playlist.canUpdate.value && (
          <ButtonLink
            key="edit"
            startIcon={<Edit />}
            to={link(`/playlists/${playlist.playlistFolderId}/playlist/${playlist.id}/design`)}
            variant="outlined"
          >
            {t('pages.shows.detail.view_zone.edit_playlist_tooltip')}
          </ButtonLink>
        ),
        <Tooltip
          key="remove"
          placement="left"
          title={t('pages.shows.detail.view_zone_playlist.remove_playlist_tooltip', {
            playlistName: playlist.name,
          })}
        >
          <Button
            color="error"
            onClick={() => onRemovePlaylist(viewZonePlaylist.id)}
            startIcon={<RemoveCircle />}
            variant="contained"
          >
            {t('pages.shows.detail.view_zone_playlist.remove_playlist_button')}
          </Button>
        </Tooltip>,
      ]
    : [
        <ShuffleButton
          key="shuffle"
          playlistId={playlist.id}
          enabled={playlist.playlistItems.length > 1}
        />,
        <ContentAddButton
          key="add"
          playlist={playlist}
          show={show}
          viewZone={viewZone}
          zoneFilter={zone}
        />,
      ];

  return (
    <Box>
      <PlaylistContainer
        banner={null}
        bulkMenu={!isReusable ? <BulkActionsMenu onRemoveContent={onRemoveBulk} /> : null}
        onRemoveContent={onRemovePlaylistItem}
        playlist={playlist}
        readOnly={isReusable}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        toolbarActions={toolbarActions}
      />
    </Box>
  );
};
